// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import { ServerResponseParser, ParsedResponse } from "./ServerResponseParse";
import { logout } from "../../state/auth/AuthEvents";
import logger from "../logger";
import { DEFAULT_500_ERROR } from "../Constant";
import { SorterResult, TablePaginationConfig } from "antd/lib/table/interface";

type RestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface DataWithId {
  id: string;
}

interface SearchDto {
  [index: string]: any;
}

export const makeRestCall = <T, U>(
  url: string,
  method: RestMethod,
  data?: T
): Promise<ParsedResponse<U>> => {
  const controller = new AbortController();
  const signal = controller.signal;
  // 5 second timeout:
  setTimeout(() => controller.abort(), 15000);
  const fetchPromise = fetch(url, {
    method,
    credentials: "include",
    signal,
    body: data ? JSON.stringify(data) : undefined,
    headers: data
      ? {
          "Content-Type": "application/json"
        }
      : {}
  });
  return fetchPromise
    .then((response: Response) => {
      logger.debug(`HTTP response for ${url} : ${response.status}`);
      if (response.status === 401) {
        logout();
        if (!url.includes("/auth/")) {
          window.location.href = "/forcedLogOut";
        }
      }
      if (response.status === 500) {
        return DEFAULT_500_ERROR;
      }
      if (method === "DELETE" && response.status < 400) {
        return {
          responseCode: response.status,
          errorMessage: undefined,
          data: undefined,
          ok: true
        };
      }
      return new ServerResponseParser<U>().parseResponse(response);
    })
    .catch(() => {
      return DEFAULT_500_ERROR;
    });
};

const buildSearchParameters = <T extends SearchDto>(dto?: T): string => {
  return dto
    ? Object.keys(dto)
        .map((key, index) =>
          dto[key] !== undefined
            ? `${index !== 0 ? "&" : ""}${key}=${String(dto[key])}`
            : ""
        )
        .join("")
    : "";
};

export const restListHandlerPagination = <U extends SearchDto, T>(
  endpointUrl: string
): ((data: {
  pagination: TablePaginationConfig;
  sorter?: SorterResult<any>;
  dto?: U;
}) => Promise<ParsedResponse<T>>) => {
  return ({
    pagination,
    sorter,
    dto
  }: {
    pagination: TablePaginationConfig;
    sorter?: SorterResult<any>;
    dto?: U;
  }) => {
    const searchParameters = buildSearchParameters<U>(dto);
    return makeRestCall<void, T>(
      `${endpointUrl}?page=${pagination.current}&pageSize=${
        pagination.pageSize
      }&sortBy=${sorter?.columnKey || ""}&ascendingOrder=${sorter?.order ===
        "ascend"}&${searchParameters}`,
      "GET"
    );
  };
};

export const restListHandler = <T>(
  endpointUrl: string
): (() => Promise<ParsedResponse<T[]>>) => {
  return () => makeRestCall<void, T[]>(endpointUrl, "GET");
};

export const restDetailsHandler = <T>(
  endpointUrl: string,
  suffixUrl?: string
): ((id: string) => Promise<ParsedResponse<T>>) => {
  return (id: string) =>
    makeRestCall<void, T>(
      endpointUrl + id + (suffixUrl ? suffixUrl : ""),
      "GET"
    );
};

export const restGetUniqueHandler = <T, U extends SearchDto = {}>(
  endpointUrl: string,
  suffixUrl?: string
): ((data: { dto?: U }) => Promise<ParsedResponse<T>>) => {
  return ({ dto }: { dto?: U }) => {
    const searchParameters = buildSearchParameters<U>(dto);
    return makeRestCall<void, T>(
      `${endpointUrl + (suffixUrl ? suffixUrl : "")}?${searchParameters}`,
      "GET"
    );
  };
};

export const restIdListHandler = <T>(
  endpointUrl: string
): ((id: string) => Promise<ParsedResponse<T[]>>) => {
  return (id: string) => makeRestCall<void, T[]>(endpointUrl + id, "GET");
};

export const restCreationHandler = <T, U>(
  endpointUrl: string
): ((data: T) => Promise<ParsedResponse<U>>) => {
  return (data: T) => makeRestCall<T, U>(endpointUrl, "POST", data);
};

export const restDeletionHandler = <T extends DataWithId>(
  endpointUrl: string
): ((data: T) => Promise<ParsedResponse<void>>) => {
  return (data: T) =>
    makeRestCall<T, void>(endpointUrl + data.id, "DELETE", data);
};

export const restDuplicationHandler = <U extends DataWithId>(
  endpointUrl: string
): ((data: { id: string }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string }) =>
    makeRestCall<void, U>(endpointUrl + data.id + "/duplicate", "POST");
};

export const restArchiveHandler = <U extends DataWithId>(
  endpointUrl: string,
  archiveUrl: string
): ((data: { id: string }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string }) =>
    makeRestCall<void, U>(endpointUrl + data.id + archiveUrl, "PATCH");
};

export const restDestroyHandler = <T extends DataWithId>(
  endpointUrl: string
): ((data: T) => Promise<ParsedResponse<void>>) => {
  return (data: T) =>
    makeRestCall<T, void>(endpointUrl + data.id + "/destroy", "DELETE", data);
};

export const restUpdateHandler = <T, U>(
  endpointUrl: string
): ((data: { id: string; dto: T }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string; dto: T }) =>
    makeRestCall<T, U>(endpointUrl + data.id, "PUT", data.dto);
};

export const restDatesUpdateHandler = <T, U>(
  endpointUrl: string
): ((data: { id: string; dto: T }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string; dto: T }) =>
    makeRestCall<T, U>(endpointUrl + data.id + "/dates", "PATCH", data.dto);
};

export const restUnpublishHandler = <T, U>(
  endpointUrl: string
): ((data: { id: string; dto: T }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string; dto: T }) =>
    makeRestCall<T, U>(endpointUrl + data.id, "PATCH", data.dto);
};

export const restWorkflowHandler = <T, U>(
  endpointUrl: string
): ((data: { id: string; dto: T }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string; dto: T }) =>
    makeRestCall<T, U>(endpointUrl + data.id + "/workflow", "POST", data.dto);
};

export const restStatusHandler = <T, U>(
  endpointUrl: string
): ((data: { id: string; dto: T }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string; dto: T }) =>
    makeRestCall<T, U>(endpointUrl + data.id + "/status", "POST", data.dto);
};

export const restWorkflowPublishHandler = <T, U>(
  endpointUrl: string
): ((data: { id: string; dto: T }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string; dto: T }) =>
    makeRestCall<T, U>(endpointUrl + data.id, "PATCH", data.dto);
};

export const restFieldUpdateHandler = <T, U>(
  endpointUrl: string,
  suffixUrl?: string
): ((data: { id: string; dto: T }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string; dto: T }) =>
    makeRestCall<T, U>(
      endpointUrl + data.id + (suffixUrl ? suffixUrl : ""),
      "PATCH",
      data.dto
    );
};

export const restIncreaseOrderHandler = <T extends DataWithId>(
  endpointUrl: string
): ((data: T) => Promise<ParsedResponse<void>>) => {
  return (data: T) =>
    makeRestCall<T, void>(endpointUrl + data.id + "/increaseOrder", "POST");
};

export const restDecreaseOrderHandler = <T extends DataWithId>(
  endpointUrl: string
): ((data: T) => Promise<ParsedResponse<void>>) => {
  return (data: T) =>
    makeRestCall<T, void>(endpointUrl + data.id + "/decreaseOrder", "POST");
};
